// src/BroucherForm.js
import React, { useState } from "react";
import {
  Box,
  TextField,
  Checkbox,
  Button,
  FormControlLabel,
  Container,
  Typography,
  Card,
  CardContent,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { franchiseAPI } from "../../utils/api";
import { getResponsiveHeight, getResponsiveSize } from "../../utils/responsive";


const BroucherForm = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [submitStatus, setSubmitStatus] = useState(null);
  const PDF_FILE_URL = `${process.env.REACT_APP_BASE_URL_1}brochure.pdf`;

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    city: "",
    agreeToPrivacyPolicy: false,
  });

  const handleChange = (event) => {
    const { name, value, checked, type } = event.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!formData.agreeToPrivacyPolicy) {
      alert("Please agree to the privacy policy.");
      return;
    }
    setSubmitStatus(null);

    // Example of an API call
    const result = await franchiseAPI.submitFranchiseApplication(formData);

    if (result.success) {
      setSubmitStatus("success");
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        city: "",
        agreeToPrivacyPolicy: false,
      });
      downloadFile();
    } else {
      setSubmitStatus("error");
      console.error("API Error:", result.error);
    }

    // Simulate a successful response (comment out if using real API)
    setTimeout(() => {
      setSubmitStatus("success");
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        city: "",
        agreeToPrivacyPolicy: false,
      });
      // Simulate PDF download
      // window.open("/path/to/brochure.pdf", "_blank");
    }, 1000);
  };

  const inputFontSize = {

              xs: "14px",
              md: "18px",
              lg: "18px",
         
  };

 
    const downloadFile = async () => {
      const fileUrl =PDF_FILE_URL;
      const fileName = 'UClean_brochure.pdf';
  
      try {
        const response = await fetch(fileUrl);
  
        // Check for CORS or network issues
        if (!response.ok) {
          throw new Error(`Failed to fetch PDF: ${response.status} ${response.statusText}`);
        }
        
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
  
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName;
        link.style.display = 'none'; // Hide the link
  
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
  
        URL.revokeObjectURL(blobUrl);
      } catch (error) {
        console.error('Error downloading PDF:', error);
        window.open(PDF_FILE_URL, '_blank');
      }
    }
  

  // const downloadFileAtURL = (url) => {
  //   const fileName = url.split("/").pop();
  //   const aTag = document.createElement("a");
  //   aTag.href = url;
  //   aTag.setAttribute("download", fileName);
  //   document.body.appendChild(aTag);
  //   aTag.click();
  //   aTag.remove();
  // };

  // Example of custom MUI styling. The font family for label/placeholder
  // is now applied globally via the theme.
  const textFieldStyles = {
    "& .MuiOutlinedInput-root": {
      borderRadius: getResponsiveSize(12),
      fontWeight: 500,
      fontSize: inputFontSize,
      "& fieldset": {
        borderColor: "rgba(0, 0, 0, 0.23)", 
      },
      "&:hover fieldset": {
        borderColor: "#B8860B", // Gold on hover
      },
      "&.Mui-focused fieldset": {
        borderColor: "#B8860B", // Gold on focus
      },
    },
    "& .MuiInputLabel-root": {
      fontSize: inputFontSize,
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: "#B8860B", // Label color when focused
      fontWeight: 500,
    },
  };

  return (
    <Container sx={{ padding: isMobile ? "0 !important" : "" }}>
      <Card
        sx={{
          px: getResponsiveHeight(isMobile ? 2 : 4),
          mt: getResponsiveHeight(4),
          borderRadius: getResponsiveSize(8),
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
        }}
      >
        <CardContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            {/* First & Last Name */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                mb: 2,
                flexDirection: isMobile ? "column" : "row",
              }}
            >
              <TextField
                fullWidth
                label="First Name"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                placeholder="First Name"
                required
                sx={{ ...textFieldStyles, flex: 1 }}
              />
              <TextField
                fullWidth
                label="Last Name"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                placeholder="Last Name"
                required
                sx={{ ...textFieldStyles, flex: 1 }}
              />
            </Box>

            {/* Email & Phone */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                mb: 2,
                flexDirection: isMobile ? "column" : "row",
              }}
            >
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Email"
                required
                sx={{ ...textFieldStyles, flex: 1 }}
              />
              <TextField
                fullWidth
                label="Phone (optional)"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Phone"
                sx={{ ...textFieldStyles, flex: 1 }}
              />
            </Box>

            {/* City Field */}
            <TextField
              fullWidth
              label="City"
              name="city"
              value={formData.city}
              onChange={handleChange}
              placeholder="City"
              sx={{ ...textFieldStyles, mb: 2 }}
            />

            {/* Privacy Policy Agreement */}
            <FormControlLabel
              control={
                <Checkbox
                  name="agreeToPrivacyPolicy"
                  checked={formData.agreeToPrivacyPolicy}
                  onChange={handleChange}
                  sx={{
                    color: "#B8860B",
                    "&.Mui-checked": {
                      color: "#B8860B",
                    },
                    "&:hover": {
                      backgroundColor: "rgba(184, 134, 11, 0.04)",
                    },
                  }}
                />
              }
              label={
                <Typography variant="regular_text_ur" sx={{ fontSize: inputFontSize }}>
                  By checking this box, I agree to the UClean Privacy Policy
                </Typography>
              }
              sx={{ mb: 2 }}
            />

            {/* Submit / Download Button */}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{
                mt: 2,
                mb: 2,
                bgcolor: "#B8860B",
                "&:hover": {
                  bgcolor: "#8B6508",
                },
                borderRadius: "25px",
                py: 1.5,
              }}
            >
              Download
            </Button>

            {submitStatus === "success" && (
              <Typography color="green" sx={{ mt: 1 }}>
                Form submitted successfully! Your download should begin
                shortly.
              </Typography>
            )}
            {submitStatus === "error" && (
              <Typography color="red" sx={{ mt: 1 }}>
                Error submitting form. Please try again.
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default BroucherForm;
